import styled from '@emotion/styled'
import { useDebouncedState, useLocalStorage } from '@mantine/hooks'
import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, GenericTopValuesByDim } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'
import { DateTime, Interval } from 'luxon'
import { FC, PropsWithChildren, ReactElement, useEffect } from 'react'
import { useAnalyticsQueryTanstack, useAnalyticsSearchTanstack } from '~/api/analytics/analytics-query.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { FilterSidebar } from '~/components/FilterSidebar/FilterSidebar'
import { FilterSidebarCategory } from '~/components/FilterSidebar/FilterSidebarCategory'
import { FilterSidebarMinorDataToggle } from '~/components/FilterSidebar/FilterSidebarMinorDataToggle'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { TimeSelectorProps } from '~/components/TimeSelector'
import { useToast } from '~/components/Toast/useToasts'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { asShort } from '~/modules/analytics/common/formulas/operation'
import { Context, contextState, Filter, filterSidebarPinnedState, filterSidebarState, TimeRange } from '~/state'
import { FilterCategory, FilterCategoryDataItem, Range } from '~/types/analytics'
import { WithClassName } from '~/types/utils'
import { filterDimensionsByUserFeatures, getTimePresets, isContextInSelectableInterval } from '~/utils/analytics'
import { displayWithCurrency } from '~/utils/string'
import { AnalyticsChartsTooltipHidden } from './AnalyticsChartsTooltip'
import { auctionCount, hbImpressionRevenue } from './common/formulas'
import { PureAnalyticsPage } from './PureAnalyticsPage'
import { useSyncContext } from './useContext'

type BuildContext = (context: Context) => Context
type AnalyticsPageProps = WithClassName & {
  timePresets?: TimeSelectorProps['presets']
  selectableInterval?: Interval
  onRefreshClick?: () => void
  comparedTo?: boolean
  filterDimensions: Dimension[]
  queryDimensions?: Dimension[]
  dashboard: AnalyticsQueryDashboard
  title?: ReactElement
  buildContext?: BuildContext
  maxNumberOfDayInTimerangeSelected?: number
  showMinorDataToggle?: boolean
  disableDatepicker?: boolean
  overrideSelectedTimeRange?: TimeRange
  hideFilterSidebar?: boolean
}

const getComputedData = (data: FilterCategoryDataItem[] | undefined, dimension: Dimension, context: Context) => {
  return data
    ? data.map((item) => {
        const hasFilter = !!(context.filters[dimension]?.values || []).length
        const inFilter = !!context.filters[dimension]?.values.find((filter) => filter.value === item.key)
        const inFilterIncluded = hasFilter && inFilter && context.filters[dimension]?.mode === 'include'
        const notInFilterExcluded = hasFilter && !inFilter && context.filters[dimension]?.mode === 'exclude'
        return {
          ...item,
          checked: !hasFilter || inFilterIncluded || notInFilterExcluded,
        }
      })
    : undefined
}

const useActions = (searchResults?: FilterCategoryDataItem[]) => {
  const [context, setContext] = useAtom(contextState)
  const [categories, setCategories] = useAtom(filterSidebarState)
  const queryClient = useQueryClient()

  const onTimeSelectionRefreshClick = () => {
    queryClient.invalidateQueries({ queryKey: ['analytics'] })
  }

  const onLabelClick = (item: FilterCategoryDataItem, category: FilterCategory) => {
    const filters: { [key in Dimension]?: Filter } = JSON.parse(JSON.stringify(context.filters))
    const existingFilters: Filter | undefined = filters[category.dimension]
    let forceSelection = true

    if (existingFilters) {
      // We force selection if filter not already in existing filters
      forceSelection = !existingFilters.values.find((filter) => filter.value === item.key)

      delete filters[category.dimension]
    }
    if (forceSelection) {
      filters[category.dimension] = {
        mode: 'include',
        values: [{ label: item.label, value: item.key, sublabel: item.subLabel }],
        name: category.dimension,
      }
    }
    const newContext: Context = { ...context, filters }
    setContext(newContext)
  }

  const onCheckboxClick = (item: FilterCategoryDataItem, category: FilterCategory) => {
    const filters: { [key in Dimension]?: Filter } = JSON.parse(JSON.stringify(context.filters))
    const filter: Filter = filters[category.dimension] || { mode: 'exclude', values: [], name: category.dimension }
    const addFacetInFilterList = filter.mode === 'exclude' ? item.checked : !item.checked
    if (addFacetInFilterList) {
      filter.values.push({ label: item.label, value: item.key, sublabel: item.subLabel })
      if (!filters[category.dimension]) {
        filters[category.dimension] = filter
      }
    } else {
      filter.values = filter.values.filter((mappedName) => mappedName.value !== item.key)
      if (filter.values.length === 0) {
        delete filters[category.dimension]
      }
    }

    const newContext: Context = { ...context, filters }
    setContext(newContext)
  }

  const onCategoryTitleClick = async (category: FilterCategory) => {
    setCategories((categories) =>
      (categories || []).map((c) =>
        c.dimension === category.dimension
          ? {
              ...category,
              open: category.expanded || !category.open,
              expanded: false,
            }
          : c
      )
    )
  }

  const onCategoryExpandToggle = (category: FilterCategory) =>
    setCategories(
      (categories || []).map((c) =>
        c === category
          ? {
              ...category,
              open: category.expanded || category.open,
              expanded: !category.expanded,
            }
          : c
      )
    )

  const onFilterClick = (dimension: Dimension) => {
    const filters: { [key in Dimension]?: Filter } = JSON.parse(JSON.stringify(context.filters))
    delete filters[dimension]
    const newContext: Context = { ...context, filters }
    setContext(newContext)
  }

  const onUpdateDateRange = (_label: string, timeRange: Range) => {
    setContext({
      ...context,
      timeRange: { ...context.timeRange, ...timeRange },
    })
  }

  const onSelectAll = (category: FilterCategory): void => {
    const filters: { [key in Dimension]?: Filter } = JSON.parse(JSON.stringify(context.filters))
    const values = searchResults?.map((item) => ({ label: item.label, value: item.key })) || []
    filters[category.dimension] = { mode: 'include', values, name: category.dimension }
    const newContext: Context = { ...context, filters }
    setContext(newContext)
  }

  return {
    onTimeSelectionRefreshClick,
    onLabelClick,
    onCheckboxClick,
    onCategoryTitleClick,
    onCategoryExpandToggle,
    onFilterClick,
    onUpdateDateRange,
    onSelectAll,
  }
}

const formatDataResponse = (category: FilterCategory, dashboard: AnalyticsQueryDashboard, currencySymbol: CurrencySymbol, values?: GenericTopValuesByDim) => {
  let data
  if (values) {
    const getValue = (index: number): number | undefined => {
      if (dashboard.filterType === 'revenues') {
        return hbImpressionRevenue.isComputable(values) ? hbImpressionRevenue.compute(values, index) : undefined
      } else {
        return auctionCount.isComputable(values) ? auctionCount.compute(values, index) : undefined
      }
    }

    data = values.name.map((name, index: number) => {
      const value = getValue(index)
      let formattedValue = '-'
      if (value) {
        formattedValue = dashboard.filterType === 'auctions' ? asShort(value) : displayWithCurrency(asShort(value), currencySymbol)
      }
      return { label: name.label, key: name.value, value, formattedValue, checked: true, subLabel: name.sublabel, trackingLabelSubject: normalizeAsTrackingLabel(category.title) }
    })
  }
  return data
}

const AnalyticsMinorFilterToggle = () => {
  const [showMinorData, setShowMinorData] = useLocalStorage<boolean>({ key: 'showMinorData', defaultValue: false })

  const onToggle = () => {
    setShowMinorData(!showMinorData)
  }
  return <FilterSidebarMinorDataToggle value={showMinorData} onToggle={onToggle} />
}

const AnalyticsCategory = ({
  category,
  queryDimensions,
  dashboard,
  buildContext,
  maxNumberOfDayInTimerangeSelected,
}: {
  category: FilterCategory
  queryDimensions: Dimension[]
  dashboard: AnalyticsQueryDashboard
  buildContext?: BuildContext
  maxNumberOfDayInTimerangeSelected: number
}) => {
  const context = useAtomValue(contextState)
  const currencySymbol = useScopeCurrency()
  const [debouncedSearch, setSearch] = useDebouncedState('', 500)
  const analyticsQuery = useAnalyticsQueryTanstack<GenericTopValuesByDim>(
    'top.values.by.dim',
    queryDimensions,
    buildContext ? buildContext(context) : context,
    {
      dimension: category.dimension,
      dashboard,
    },
    true,
    category.open && isContextInSelectableInterval(context, maxNumberOfDayInTimerangeSelected)
  )

  const analyticsQuerySearch = useAnalyticsSearchTanstack<GenericTopValuesByDim>(
    queryDimensions,
    buildContext ? buildContext(context) : context,
    {
      dimension: category.dimension,
      term: debouncedSearch,
      dashboard,
    },
    true,
    category.expanded && isContextInSelectableInterval(context, maxNumberOfDayInTimerangeSelected)
  )
  const searchResults = getComputedData(formatDataResponse(category, dashboard, currencySymbol, analyticsQuerySearch?.data), category.dimension, context)
  const { onLabelClick, onCheckboxClick, onCategoryTitleClick, onCategoryExpandToggle, onSelectAll } = useActions(searchResults)
  const computedData = getComputedData(formatDataResponse(category, dashboard, currencySymbol, analyticsQuery?.data), category.dimension, context)
  return (
    <FilterSidebarCategory
      data={category.expanded ? searchResults : computedData}
      category={category}
      onSearch={setSearch}
      onSelectAll={onSelectAll}
      loading={analyticsQuery.isFetching}
      onTitleClick={onCategoryTitleClick}
      onExpandToggle={onCategoryExpandToggle}
      onLabelClick={onLabelClick}
      onCheckboxClick={onCheckboxClick}
    />
  )
}

const BaseAnalyticsPage: FC<PropsWithChildren<AnalyticsPageProps>> = ({
  className,
  onRefreshClick,
  comparedTo,
  children,
  timePresets: overrideTimePresets,
  selectableInterval: overrideSelectableInterval,
  filterDimensions,
  queryDimensions,
  dashboard,
  title,
  buildContext,
  maxNumberOfDayInTimerangeSelected = 93,
  showMinorDataToggle,
  disableDatepicker,
  overrideSelectedTimeRange,
  hideFilterSidebar,
}) => {
  const [context, setContext] = useAtom(contextState)
  const toast = useToast()
  const today = DateTime.now().startOf('day')
  const timePresets = overrideTimePresets ?? getTimePresets(today)
  const selectableInterval = overrideSelectableInterval ?? Interval.fromDateTimes(today.minus({ days: 93 }), today.plus({ days: 1 }))
  const categories = useAtomValue(filterSidebarState)
  const user = useAuthenticatedUser()
  const filteredFilterDimensions = filterDimensionsByUserFeatures(filterDimensions, user)
  const filteredQueryDimensions = queryDimensions ? filterDimensionsByUserFeatures(queryDimensions, user) : undefined

  const { onFilterClick, onUpdateDateRange, onTimeSelectionRefreshClick } = useActions()
  const syncContext = useSyncContext() // TODO NRA CFO why don't use the syntax (without useEffect) : useSyncContext(context)

  const filteredCategories = categories.filter((category) => filteredFilterDimensions.includes(category.dimension))
  const analyticsCategories = filteredCategories.map((category, index) => (
    <AnalyticsCategory
      key={index}
      category={category}
      queryDimensions={filteredQueryDimensions ?? filteredFilterDimensions}
      dashboard={dashboard}
      buildContext={buildContext}
      maxNumberOfDayInTimerangeSelected={maxNumberOfDayInTimerangeSelected}
    />
  ))
  const expandedCategoryIndex = filteredCategories.findIndex(({ expanded }) => expanded)
  const expandedCategory = analyticsCategories[expandedCategoryIndex]
  useEffect(() => {
    let isMounted = true
    const latestDay = selectableInterval.end.endOf('day').minus({ days: 1 })
    if (!isContextInSelectableInterval(context, maxNumberOfDayInTimerangeSelected, latestDay)) {
      const { tz } = context.timeRange
      if (isMounted) {
        toast('The time range has been replaced by the latest available day as the previous selection was not available on this dashboard.')
        setContext((context) => ({ ...context, timeRange: { tz, from: latestDay.startOf('day').toLocal(), to: latestDay.endOf('day').toLocal() } }))
      }
    }

    if (isMounted) {
      syncContext(context)
    }

    return () => {
      isMounted = false
    }
  }, [context, selectableInterval])

  const [filterPinned, setFilterPinned] = useAtom(filterSidebarPinnedState)

  return (
    <PureAnalyticsPage
      className={className}
      onRefreshClick={() => {
        onTimeSelectionRefreshClick()
        return onRefreshClick && onRefreshClick()
      }}
      onUpdate={onUpdateDateRange}
      disableDatepicker={disableDatepicker}
      selectedRange={overrideSelectedTimeRange || context.timeRange}
      today={today}
      timePresets={timePresets}
      comparedTo={!!comparedTo}
      filters={context.filters}
      filterDimensions={filteredFilterDimensions}
      onFilterClick={onFilterClick}
      filterSidebar={
        hideFilterSidebar ? undefined : (
          <FilterSidebar
            minorFilterToggle={showMinorDataToggle ? <AnalyticsMinorFilterToggle /> : undefined}
            categories={analyticsCategories}
            expandedCategory={expandedCategory}
            isPinned={filterPinned.pinned}
            onPin={() => {
              setFilterPinned({ pinned: !filterPinned.pinned })
              setTimeout(() => {
                window.dispatchEvent(new Event('resize'))
              }, 400)
            }}
          />
        )
      }
      isFilterSidebarPinned={filterPinned.pinned}
      selectableInterval={selectableInterval}
      title={title}
      timezone={context.timeRange.tz}
      maxNumberOfDayInTimerangeSelected={maxNumberOfDayInTimerangeSelected}
    >
      {/* TODO cfo 2022-11-29 - Remove once emotion is fully compatible with React18 */}
      <AnalyticsChartsTooltipHidden />
      {/* TODO END */}
      {children}
    </PureAnalyticsPage>
  )
}

export const AnalyticsPage = styled(BaseAnalyticsPage)``
